import gsap from "gsap"

/**
 * animateTrickResult
 *
 * After both players have thrown their card for the current trick,
 * this function finds the two center-card DOM elements for that trick,
 * determines which belongs to the winner, and:
 *
 *  1. Raises the winner card's z-index above the loser card.
 *  2. Animates the winner card sliding on top of the loser card
 *     (a slight nudge + z-stack so it is visually "on top").
 *  3. Dims / darkens the loser card with a CSS filter so the winner
 *     card is clearly the dominant one.
 *
 * @param {object} params
 * @param {number}   params.trickCount      - 1-based trick number that just finished
 * @param {number}   params.winnerSeatIndex - display-seat index (0-based) of the winner
 * @param {number}   params.totalSeats      - total number of seats at the table
 */
export function animateTrickResult({ trickCount, winnerSeatIndex, totalSeats }) {
    // Center cards are rendered with id:
    //   truco-center-card-{arrayIndex}-{trickCount}-{seatIndex}
    // We select all cards for the current trick regardless of array index.
    const allCenterCards = Array.from(
        document.querySelectorAll(`[id^="truco-center-card-"]`)
    )

    // Filter to cards that belong to this trick
    const trickCards = allCenterCards.filter(el => {
        const parts = el.id.split("-")
        // id pattern: truco-center-card-{i}-{trickCount}-{seatIndex}
        // parts:       0     1      2    3    4              5
        const elTrickCount = parseInt(parts[4], 10)
        return elTrickCount === trickCount
    })

    if (trickCards.length < 2) return // nothing to do if second card not yet in DOM

    // Identify winner and loser card elements
    const winnerEl = trickCards.find(el => {
        const parts = el.id.split("-")
        return parseInt(parts[5], 10) === winnerSeatIndex
    })
    const loserEl = trickCards.find(el => el !== winnerEl)

    if (!winnerEl || !loserEl) return

    // Parse current transform translate to get loser position
    const loserTransform  = new DOMMatrix(getComputedStyle(loserEl).transform)
    const winnerTransform = new DOMMatrix(getComputedStyle(winnerEl).transform)

    const loserX  = loserTransform.m41  || 0
    const loserY  = loserTransform.m42  || 0

    // Winner card slides on top of the loser card with a slight scale-up
    // then settles. The loser gets dimmed.
    const winnerZ = parseInt(winnerEl.style.zIndex || 1000, 10)
    const loserZ  = parseInt(loserEl.style.zIndex  || 999,  10)

    // Ensure winner is painted above loser
    gsap.set(winnerEl, { zIndex: Math.max(winnerZ, loserZ) + 1 })

    const tl = gsap.timeline()

    // 1. Briefly lift the winner card (scale up)
    tl.to(winnerEl, {
        scale: 1.08,
        duration: 0.15,
        ease: "power2.out"
    })

    // 2. Slide winner card onto loser card position
    tl.to(winnerEl, {
        // x: loserX + 6,   // slight offset so both cards are visible
        // y: loserY - 6,
        scale: 1,
        duration: 0.35,
        ease: "power3.out"
    })

    // 3. Dim the loser card simultaneously with the slide
    tl.to(loserEl, {
        filter: "brightness(0.55)",
        duration: 0.35,
        ease: "power2.out"
    }, "<")  // "<" = start at the same time as previous tween

    return tl
}
